/**
 * AdminOrdersTab — Platform-wide orders view for the Super Admin.
 *
 * Reads from `/api/admin/orders` — server-side paginated, works on any
 * DB size. Shows order number, customer, business, amount, status, and
 * date. Not a placeholder — every field is live data.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/App";

const STATUS_TONES = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  succeeded: "bg-emerald-50 text-emerald-700 border-emerald-200",
  shipped: "bg-sky-50 text-sky-700 border-sky-200",
  processing: "bg-amber-50 text-amber-800 border-amber-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  refunded: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminOrdersTab() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 25;

  const fetchOrders = useCallback(async (p) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orders`, {
        params: { page: p, limit },
        withCredentials: true,
      });
      setRows(r.data.orders || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(page); }, [fetchOrders, page]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="admin-orders-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-2xl text-tbc-navy flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-tbc-gold" /> Orders
          </h2>
          <p className="text-xs text-tbc-navy/60 mt-1">Platform-wide order feed. {total.toLocaleString()} total.</p>
        </div>
      </div>

      <div className="bg-white border border-tbc-navy/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-tbc-navy/[0.03] text-tbc-navy/70 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-4 py-3 text-left">Order</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Amount</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-tbc-navy/50">
                  <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading orders…
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-tbc-navy/50">
                  No orders yet.
                </td></tr>
              ) : rows.map((o) => {
                const status = (o.status || o.payment_status || "pending").toLowerCase();
                const tone = STATUS_TONES[status] || "bg-slate-100 text-slate-700 border-slate-200";
                return (
                  <tr key={o.order_id} className="border-t border-tbc-navy/5" data-testid={`admin-order-row-${o.order_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-tbc-navy">{o.order_id?.slice(0, 16) || "—"}</td>
                    <td className="px-4 py-3 text-tbc-navy/80">{o.customer_email || o.user_email || "—"}</td>
                    <td className="px-4 py-3 text-tbc-navy font-semibold">${((o.total_cents || o.amount_cents || 0) / 100).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest ${tone}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-tbc-navy/60 text-xs">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-tbc-navy/60">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-full border border-tbc-navy/20 px-3 py-1 disabled:opacity-40"
              data-testid="admin-orders-prev"
            ><ChevronLeft className="w-3.5 h-3.5" /> Prev</button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 rounded-full border border-tbc-navy/20 px-3 py-1 disabled:opacity-40"
              data-testid="admin-orders-next"
            >Next <ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
